// @ts-strict-ignore
import {useEffect, useMemo, useState} from 'react';
import orderBy from 'lodash/orderBy';

import {useTranslation} from 'common/i18n/hooks';
import {ExpansionToggleLabelMode, Section, SectionItem, SelectedTheme} from 'common/components/items-menu/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {useEventsController} from 'common/contexts/react/events-controller-context';
import {TagItem} from 'common/components/tag-input/tag-item';
import {useCohorts} from 'common/contexts/react/cohorts-context';
import {
  fetchAllPropertyDefinitionsForGroupProfiles,
  fetchPropertyDefinitionsForUserProfiles,
  groupPropertyDefinitionsStore,
  peoplePropertyDefinitionsStore,
} from 'common/report/stores/property-definitions';
import {useDataGroups} from 'common/contexts/react/data-group-context';
import {Translatable} from 'common/i18n';
import {BehaviorEntity as Behavior, BehaviorEntity} from 'app_api/projects/behaviors/types';
import {getDataGroupLabel} from 'common/report/insights/util/events';
import {MetricType} from 'common/types/reports/bookmark';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {LocalizationStore} from 'common/i18n/store';
import {ICON_MAPPING} from 'common/query-builder/query-entry-section/util/entry-icon';
import {BehaviorMetric, Metric} from 'app_api/projects/metrics/types';
import {findKeyByValue} from 'common/util/typescript';
import {BehaviorTypeIcons, getIconForSavedMetric} from 'common/report/insights/util/metrics';
import {SPECIAL_EVENTS} from 'common/report/constants';

import {
  getEventSelectedLabel,
  getEventsSections,
  getSearchSelectedTagItems,
  getSearchTagMenuSections,
  getTagsSection,
  isEventItem,
} from '../events-menu/util';
import {useChildMenuCache} from '../child-menu';
import {EventItem, EventMenuItem} from '../events-menu/types';
import {getBehaviorMenuItem, useSavedBehaviors} from '../behaviors-menu/utils';
import {getFormulaSections, useSavedFormulas} from '../formulas-menu/utils';
import {getSerializedCohorts} from '../properties-menu/util';
import {template as noTagsTemplate} from '../events-menu/no-tags';
import {FormulaMenuItemValue} from '../formulas-menu/types';
import {TOP_PEOPLE} from '../properties-menu/constants';
import {NUM_RECENT_ITEMS} from '../constants';
import {isSavedCohort} from '../properties-menu/types';
import {convertDataGroupIdToSet} from '../utils/data-group-util';
import {
  BehaviorMenuTypes,
  CategoryToMetricType,
  CohortItemValue,
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  isSimpleMenuSectionItem,
  MenuType,
  MenuTypeMap,
  MenuTypeToMetricType,
  MultiMetricsSection,
  MultiMetricsSectionItem,
  ProfileItemValue,
  SavedMetricMenuItemValue,
  SavedMetricSectionItem,
  SidebarCategory,
  SidebarItem,
  SidebarItemType,
} from './types';
import {getCohortSections, getHistoricalProperties, getProfileSections, getRecentItems} from './utils';
import {getMetricsSections, useSavedMetrics} from './metrics';

export function useSelectedLabel({
  selected,
  dataGroupId,
}: {
  selected: MultiMetricsSectionItem;
  dataGroupId: string;
}): Translatable {
  const metrics = useMetrics();
  const dataGroupStore = useDataGroups();
  const cohortsStore = useCohorts();

  if (!selected) {
    return ({t}) => t(`query-builder.select-metric`);
  }

  let label: Translatable = ({t}) => t(`query-builder.select-metric`);
  if (isFunnelMenuSectionItem(selected) || isRetentionMenuSectionItem(selected) || isSimpleMenuSectionItem(selected)) {
    label = selected.value.name;
  } else if (isFormulaMenuSectionItem(selected)) {
    label =
      selected.value.name ||
      (selected.value.id && metrics.getOne(selected.value.id)?.value?.name) ||
      (({t}) => t(`common.loading`));
  } else if (isEventMenuSectionItem(selected) && isEventItem(selected.value)) {
    label = getEventSelectedLabel(selected.value, {
      includeNameOnRename: true,
      labelPrefix: selected.value?.labelPrefix,
    });
  } else if (isCohortMenuSectionItem(selected)) {
    if (isSavedCohort(selected.value)) {
      label = cohortsStore?.value[selected.value.value]?.name || selected.value.label;
    } else {
      label = selected.value.label;
    }
  } else if (isProfileMenuSectionItem(selected)) {
    if (dataGroupId) {
      const dataGroups = Object.values(dataGroupStore.value);
      label = getDataGroupLabel(dataGroupId, dataGroups || []);
    } else {
      label = TOP_PEOPLE.label;
    }
  } else if (isSavedMetricMenuSectionItem(selected)) {
    label = selected.value.name;
  }

  return label;
}

export function useSidebarSections({
  excludedMetricTypes,
  selectedCategory,
  selectedTagSet,
  dataGroupName,
  metricLevelDataGroups,
  isSidebarCollapsed,
  isSidebarTagSectionExpanded,
  excludeCustomEvents,
}: {
  excludedMetricTypes: Set<MetricType>;
  selectedCategory: SidebarCategory;
  selectedTagSet: Set<string>;
  dataGroupName: string;
  metricLevelDataGroups: boolean;
  isSidebarCollapsed: boolean;
  isSidebarTagSectionExpanded: boolean;
  excludeCustomEvents: boolean;
}): Array<Section<SidebarItem>> {
  type SidebarSectionItem = SectionItem<SidebarItem, Obj<any>>;

  const eventsController = useEventsController();
  const i18n = useTranslation();
  const currentProject = useCurrentProject();
  const isVerifiedContentEnabled = currentProject.get()?.value?.canUseVerifiedContent();

  const {t} = i18n;
  const tagsSection = getTagsSection(eventsController);

  const iconMap = BehaviorTypeIcons;

  let sidebarTagSection: Section<SidebarItem> | undefined;
  const isCategoryWithEvents = [
    SidebarCategory.All,
    SidebarCategory.Event,
    SidebarCategory.Verified,
    SidebarCategory.CustomEvent,
  ].includes(selectedCategory);
  if (isCategoryWithEvents) {
    if (!isSidebarCollapsed) {
      sidebarTagSection = {
        icon: `tag`,
        label: t(`query-builder.events-menu.sidebar-tags`),
        selectedTheme: SelectedTheme.Checkmark,
        expansionToggle: {
          isExpanded: isSidebarTagSectionExpanded,
          labelMode: ExpansionToggleLabelMode.Unchanged,
          alwaysShow: true,
        },
        items: tagsSection.items.map((tag): SectionItem<SidebarItem> => {
          return {
            label: tag.label,
            isSelected: selectedTagSet.has(tag.value.value),
            value: {
              type: SidebarItemType.EventTag,
              tag: tag.value.value,
            },
          };
        }),
        emptySectionRenderer: noTagsTemplate,
      };
    } else {
      sidebarTagSection = {
        items: [
          {
            icon: `tag`,
            label: ``,
            tooltip: {text: t(`query-builder.events-menu.sidebar-tags`), 'max-width': `250px`},
            value: {
              type: SidebarItemType.EventTagAdder,
            },
          },
        ],
      };
    }
  }

  type SidebarCategoryItem = {
    label: string;
    category: SidebarCategory;
    icon: string;
    tooltipText?: string;
  };

  const createSidebarSectionItem = (item: SidebarCategoryItem): SidebarSectionItem => {
    let tooltipText: string | undefined;
    if (isSidebarCollapsed) {
      if (item.tooltipText) {
        tooltipText = `${item.label}: ${item.tooltipText}`;
      } else {
        tooltipText = item.label;
      }
    } else {
      tooltipText = item.tooltipText;
    }

    return {
      label: isSidebarCollapsed ? `` : item.label,
      value: {
        type: SidebarItemType.Category,
        category: item.category,
      },
      icon: item.icon,
      tooltip: tooltipText
        ? {
            text: tooltipText,
            'max-width': `250px`,
          }
        : undefined,
      isSelected: selectedCategory === item.category,
    };
  };

  const allItem = createSidebarSectionItem({
    label: t(`query-builder.events-menu.sidebar-all`),
    icon: `icon-grid`,
    category: SidebarCategory.All,
  });

  let verifiedItem: SidebarSectionItem | undefined;
  if (isVerifiedContentEnabled) {
    verifiedItem = createSidebarSectionItem({
      label: t(`query-builder.events-menu.sidebar-verified`),
      icon: `verified-outlined`,
      category: SidebarCategory.Verified,
    });
  }

  let eventItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Event])) {
    eventItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.event`),
      icon: `event`,
      tooltipText: t(`query-builder.metrics.event-tooltip`),
      category: SidebarCategory.Event,
    });
  }

  let profileItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Profile])) {
    profileItem = createSidebarSectionItem({
      label: metricLevelDataGroups ? t(`mixology.profile_plural`) : dataGroupName,
      icon: iconMap[MetricType.People],
      tooltipText: t(`query-builder.metrics.profile-tooltip`),
      category: SidebarCategory.Profile,
    });
  }

  let savedMetricItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Metric])) {
    savedMetricItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.metric`),
      icon: ICON_MAPPING[MetricType.SavedMetric],
      tooltipText: t(`query-builder.metrics.saved-metric-tooltip`),
      category: SidebarCategory.Metric,
    });
  }

  let formulaItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Formula])) {
    formulaItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.formula`),
      icon: ICON_MAPPING[MetricType.Formula],
      tooltipText: t(`query-builder.metrics.formula-tooltip`),
      category: SidebarCategory.Formula,
    });
  }

  let funnelItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Funnel])) {
    funnelItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.funnel`),
      icon: iconMap[MetricType.Funnel],
      tooltipText: t(`query-builder.metrics.funnel-tooltip`),
      category: SidebarCategory.Funnel,
    });
  }

  let retentionItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Retention])) {
    retentionItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.retention`),
      icon: iconMap[MetricType.Retention],
      tooltipText: t(`query-builder.metrics.retention-tooltip`),
      category: SidebarCategory.Retention,
    });
  }

  let simpleItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Simple])) {
    simpleItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.simple`),
      icon: iconMap[MetricType.Simple],
      tooltipText: t(`query-builder.metrics.simple-tooltip`),
      category: SidebarCategory.Simple,
    });
  }

  let cohortItem: SidebarSectionItem | undefined;
  if (!excludedMetricTypes.has(CategoryToMetricType[SidebarCategory.Cohort])) {
    cohortItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.cohort`),
      icon: `community`,
      tooltipText: t(`query-builder.metrics.cohort-tooltip`),
      category: SidebarCategory.Cohort,
    });
  }

  let customEventItem: SidebarSectionItem | undefined;
  if (!excludeCustomEvents) {
    customEventItem = createSidebarSectionItem({
      label: t(`query-builder.metrics.custom-event`),
      icon: `custom-event-beta`,
      tooltipText: t(`query-builder.metrics.custom-event-tooltip`),
      category: SidebarCategory.CustomEvent,
    });
  }

  return [
    {
      selectedTheme: SelectedTheme.Focus,
      items: (
        [
          allItem,
          eventItem,
          simpleItem,
          funnelItem,
          retentionItem,
          savedMetricItem,
          formulaItem,
          profileItem,
          cohortItem,
          verifiedItem,
          customEventItem,
        ] satisfies Array<SidebarSectionItem | undefined>
      ).filter(Boolean),
    },
    ...(sidebarTagSection ? [sidebarTagSection] : []),
  ];
}

export function useSearchSelectedTagItems({selectedTagSet}): TagItem[] {
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const tagItems = useMemo(
    () =>
      getSearchSelectedTagItems({
        selectedTagSet,
        i18n,
        eventsController,
      }),
    [selectedTagSet],
  );
  return tagItems;
}

export function useSearchTagMenuSections({selectedTagSet}): Array<Section<TagItem>> {
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const sections = useMemo(
    () =>
      getSearchTagMenuSections({
        selectedTagSet,
        i18n,
        eventsController,
      }),
    [selectedTagSet],
  );
  return sections;
}

function useEventsSections({
  excludedMetricTypes,
  dataGroupId,
  excludeAutotrackEvents,
  excludeCustomEvents,
  searchFilter,
  searchVerified = false,
  selectedTagSet,
  selected,
  showAllEvents,
  showAnyEvent,
  showTopEvents,
  showSessionEvent,
  showSpecialDatasetProperty,
  userId,
  projectId,
  workspaceId,
}: {
  excludedMetricTypes?: Set<MetricType>;
  dataGroupId: string;
  excludeAutotrackEvents: boolean;
  excludeCustomEvents: boolean;
  searchFilter: string;
  searchVerified: boolean;
  selectedTagSet: Set<string>;
  selected: MultiMetricsSectionItem;
  showAllEvents: boolean;
  showAnyEvent: boolean;
  showTopEvents: boolean;
  showSessionEvent: boolean;
  showSpecialDatasetProperty: boolean;
  userId: number;
  projectId: number;
  workspaceId: number;
}): Array<MultiMetricsSection<EventMenuItem>> {
  if (excludedMetricTypes?.has(MetricType.Event)) {
    return [];
  }

  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  const eventsController = useEventsController();
  const i18n = useTranslation();

  const {print} = i18n;
  const sectionLabel: Translatable = ({t}) => t(`query-builder.multi-metrics-menu.section-events`);
  let recentSectionItems = getRecentItems(cache, MenuType.Event);
  if (searchVerified) {
    recentSectionItems = recentSectionItems.filter(
      (sectionItem) => isEventItem(sectionItem.value) && !!sectionItem.value?.verified,
    );
  }
  const eventsSections = getEventsSections({
    recentlyUsedEvents: recentSectionItems.map((sectionItem) => sectionItem.value as EventItem),
    eventsController,
    dataGroupIds: convertDataGroupIdToSet(dataGroupId),
    excludeAutotrackEvents,
    excludeCustomEvents,
    mainSectionLabel: print(sectionLabel),
    searchFilter,
    selectedTagSet,
    selected: selected && isEventMenuSectionItem(selected) ? (selected.value as EventItem) : null,
    showAllEvents,
    showAnyEvent,
    showTopEvents,
    showSessionEvent,
    showSpecialDatasetProperty,
    otherOptions: [],
    searchVerified,
  });

  return eventsSections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      menuType: MenuType.Event,
    })),
  }));
}

function useProfileSections({
  dataGroupId,
  metricLevelDataGroups,
  dataGroupIdToNameMap,
  excludedMetricTypes,
  i18n,
}: {
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
  dataGroupIdToNameMap: {[id: string]: string};
  excludedMetricTypes: Set<MetricType>;
  i18n: LocalizationStore;
}): {
  isLoading: boolean;
  sections: Array<MultiMetricsSection<ProfileItemValue>>;
} {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dataGroupStore = useDataGroups();

  const historicalProperties = useMemo(() => {
    if (isLoading) return [];
    if (metricLevelDataGroups) {
      // for every data group plus the user group, get historical properties
      const dataGroupIds = [``, ...Object.keys(dataGroupStore.value)];
      return dataGroupIds.reduce((acc, id) => {
        acc[id] = getHistoricalProperties(id, i18n);
        return acc;
      }, {});
    }
    return {[dataGroupId || ``]: getHistoricalProperties(dataGroupId, i18n)};
  }, [
    dataGroupId,
    metricLevelDataGroups,
    isLoading,
    Object.keys(dataGroupStore.value).length,
    Object.keys(groupPropertyDefinitionsStore.value).length,
    Object.keys(peoplePropertyDefinitionsStore.value).length,
  ]);

  useEffect(() => {
    const fetchGroupPropsIfMissing = async () => {
      if (Object.keys(groupPropertyDefinitionsStore.value).length === 0) {
        await fetchAllPropertyDefinitionsForGroupProfiles(dataGroupStore.value);
      }
    };
    const fetchUserPropsIfMissing = async () => {
      if (Object.keys(peoplePropertyDefinitionsStore.value).length === 0) {
        await fetchPropertyDefinitionsForUserProfiles({});
      }
    };

    const fetchProfileProperties = async () => {
      setIsLoading(true);
      if (metricLevelDataGroups) {
        await Promise.all([fetchGroupPropsIfMissing(), fetchUserPropsIfMissing()]);
      } else if (dataGroupId) {
        await fetchGroupPropsIfMissing();
      } else {
        await fetchUserPropsIfMissing();
      }
      setIsLoading(false);
    };
    fetchProfileProperties();
  }, [dataGroupId, metricLevelDataGroups]);

  if (excludedMetricTypes.has(MetricType.People)) {
    return {isLoading, sections: []};
  }
  const sections = getProfileSections({
    dataGroupIdToNameMap,
    metricLevelDataGroups,
    historicalProperties,
  });
  return {isLoading, sections};
}

function useMetricsSections({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  searchVerified,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  searchVerified: boolean;
}): Array<MultiMetricsSection<Metric>> {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  let savedMetrics = useSavedMetrics(excludedMetricTypes);
  let recentSectionItems = getRecentItems(cache, MenuType.SavedMetric).slice(0, NUM_RECENT_ITEMS);

  if (searchVerified) {
    savedMetrics = savedMetrics.filter((metric) => {
      return !!metric.verified;
    });
    recentSectionItems = recentSectionItems.filter((sectionItem) => !!sectionItem.value?.verified);
  }

  return getMetricsSections({
    recentMetricItems: recentSectionItems.map((sectionItem) => sectionItem.value),
    savedMetrics,
    excludedBehaviorTypes: excludedMetricTypes,
  });
}

function useFormulaSections({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  searchVerified,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  searchVerified: boolean;
}): Array<MultiMetricsSection<FormulaMenuItemValue>> {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  let savedFormulas = useSavedFormulas();
  let recentSectionItems = getRecentItems(cache, MenuType.Formula);

  if (excludedMetricTypes.has(MetricType.Formula)) {
    return [];
  }

  if (searchVerified) {
    savedFormulas = savedFormulas.filter((formula) => {
      return !!formula.verified;
    });
    recentSectionItems = recentSectionItems.filter((sectionItem) => !!sectionItem.value?.verified);
  }

  return getFormulaSections({
    recentFormulaMenuItems: recentSectionItems.map((sectionItem) => sectionItem.value),
    savedFormulas,
  });
}

function getMetricMenuItem(metric: BehaviorMetric): SavedMetricSectionItem {
  const icon = getIconForSavedMetric(metric);
  return {
    icon,
    label: metric.name,
    menuType: MenuType.SavedMetric,
    value: metric,
  };
}

function useMetricAndBehaviorSection({
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
  metricType,
}: {
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
  metricType: MetricType.Funnel | MetricType.Retention | MetricType.Simple;
}): Array<MultiMetricsSection<BehaviorMetric | Behavior>> {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});
  const savedBehaviors = useSavedBehaviors({metricType});
  const menuType = findKeyByValue(MenuTypeToMetricType, metricType) as BehaviorMenuTypes;

  let savedMetrics = useSavedMetrics(excludedMetricTypes);
  savedMetrics = savedMetrics.filter(
    (metric) => metric.type === `metric` && metric.definition?.behavior?.type === metricType,
  );

  if (excludedMetricTypes.has(metricType)) {
    return [];
  }

  const recentItems: Array<
    | MenuTypeMap[MenuType.SavedMetric]
    | MenuTypeMap[MenuType.Funnel]
    | MenuTypeMap[MenuType.Simple]
    | MenuTypeMap[MenuType.Retention]
  > = [MenuType.SavedMetric, menuType]
    .filter((type) => !excludedMetricTypes.has(MenuTypeToMetricType[type]))
    .flatMap((type: MenuType.Funnel | MenuType.Retention | MenuType.Simple | MenuType.SavedMetric) =>
      getRecentItems<typeof type>(cache, type),
    )
    .map((item) => {
      if (item.value.type === `metric`) {
        const recentMetric = savedMetrics.find((metric) => metric.id === item.value.id);
        return recentMetric && getMetricMenuItem(recentMetric);
      }
      const recentBehavior = savedBehaviors.find((behavior) => behavior.id === item.value.id);
      return recentBehavior && getBehaviorMenuItem(recentBehavior, metricType);
    })
    .filter(Boolean);

  let recentSection: MultiMetricsSection<BehaviorMetric | Behavior> | undefined;
  const showRecents = recentItems.length > 0;
  if (showRecents) {
    recentSection = {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
      isRecentSection: true,
      items: recentItems,
    };
  }

  const mainSectionItems = orderBy([...savedBehaviors, ...savedMetrics], (item) => item.created, `desc`).map(
    (savedItem) => {
      if (savedItem.type === `metric`) {
        return getMetricMenuItem(savedItem);
      }
      return getBehaviorMenuItem(savedItem, metricType);
    },
  );

  const mainSection: MultiMetricsSection<BehaviorMetric | Behavior> = {
    label: ({t}) => t(`query-builder.multi-metrics-menu.section-${metricType}-metrics-and-behaviors`),
    items: mainSectionItems,
  };

  return [recentSection, mainSection].filter(Boolean);
}

function useCohortsSections({
  dataGroupId,
  metricLevelDataGroups,
  dataGroupIdToNameMap,
  excludedMetricTypes,
  userId,
  projectId,
  workspaceId,
}: {
  dataGroupId: string;
  metricLevelDataGroups: boolean;
  dataGroupIdToNameMap: {[id: string]: string};
  excludedMetricTypes: Set<MetricType>;
  userId: number;
  projectId: number;
  workspaceId: number;
}): {
  isLoading: boolean;
  sections: Array<MultiMetricsSection<CohortItemValue>>;
} {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const cohortsStore = useCohorts();
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});

  const cohortsByDataGroupId = useMemo(() => {
    if (isLoading) return [];
    const dataGroupIds = metricLevelDataGroups ? Object.keys(dataGroupIdToNameMap) : [dataGroupId];
    return dataGroupIds.reduce((acc, id) => {
      const cohorts = getSerializedCohorts(cohortsStore, id);
      if (cohorts.length > 0) {
        acc[id] = cohorts;
      }
      return acc;
    }, {});
  }, [dataGroupId, isLoading, Object.values(cohortsStore.value).length]);
  const recentSectionItems = getRecentItems(cache, MenuType.Cohort);

  useEffect(() => {
    const fetchCohorts = async () => {
      setIsLoading(true);
      const params = {projectId, workspaceId};
      if (metricLevelDataGroups) {
        // for every data group plus the user group, get cohorts
        const dataGroupIds = Object.keys(dataGroupIdToNameMap);
        await Promise.all(
          dataGroupIds.map((id) => {
            if (id === ``) {
              return cohortsStore.fetch(params);
            }
            return cohortsStore.fetch({...params, dataGroupId: id}, {}, id);
          }),
        );
      } else if (dataGroupId) {
        await cohortsStore.fetch({...params, dataGroupId}, {}, dataGroupId);
      } else {
        await cohortsStore.fetch(params);
      }
      setIsLoading(false);
    };
    fetchCohorts();
  }, [dataGroupId]);

  if (excludedMetricTypes.has(MetricType.Cohort)) {
    return {isLoading, sections: []};
  }
  const sections = getCohortSections({
    recentCohortSectionItems: recentSectionItems,
    dataGroupIdToNameMap,
    userId,
    cohortsByDataGroupId,
    metricLevelDataGroups,
  });
  return {isLoading, sections};
}

export function useMultiMetricsSections({
  category,
  dataGroupId,
  metricLevelDataGroups,
  excludedMetricTypes,
  excludeAutotrackEvents,
  excludeCustomEvents,
  searchFilter,
  searchVerified,
  selectedTagSet,
  selected,
  showAllEvents,
  showAnyEvent,
  showTopEvents,
  showSessionEvent,
  showSpecialDatasetProperty,
  userId,
  projectId,
  workspaceId,
  i18n,
}: {
  category: SidebarCategory;
  dataGroupId: string;
  metricLevelDataGroups: boolean;
  excludedMetricTypes: Set<MetricType>;
  excludeAutotrackEvents: boolean;
  excludeCustomEvents: boolean;
  searchFilter: string;
  searchVerified: boolean;
  selectedTagSet: Set<string>;
  selected: MultiMetricsSectionItem;
  showAllEvents: boolean;
  showAnyEvent: boolean;
  showTopEvents: boolean;
  showSessionEvent: boolean;
  showSpecialDatasetProperty: boolean;
  userId: number;
  projectId: number;
  workspaceId: number;
  i18n: LocalizationStore;
}): {
  isLoading: boolean;
  sections: MultiMetricsSection[];
} {
  const cache = useChildMenuCache({childMenuName: `multi-metrics-menu`, userId, projectId, workspaceId});

  const behaviors = useBehaviors();
  const metrics = useMetrics();
  const eventsController = useEventsController();
  const {t} = useTranslation();
  const dataGroupStore = useDataGroups();
  const dataGroupIdToNameMap = useMemo(() => {
    const names = {'': t(`query-builder.dg-user`)};
    for (const [id, dataGroup] of Object.entries(dataGroupStore.value || {})) {
      names[id] = dataGroup.displayName;
    }
    return names;
  }, [Object.keys(dataGroupStore.value).length]);

  const eventsSections = useEventsSections({
    dataGroupId,
    excludeAutotrackEvents,
    excludeCustomEvents,
    searchFilter,
    searchVerified,
    selectedTagSet,
    selected,
    showAllEvents,
    showAnyEvent,
    showTopEvents,
    showSessionEvent,
    showSpecialDatasetProperty,
    userId,
    projectId,
    workspaceId,
    excludedMetricTypes,
  });
  const {isLoading: isLoadingProperties, sections: profileSections} = useProfileSections({
    dataGroupId,
    metricLevelDataGroups,
    dataGroupIdToNameMap,
    excludedMetricTypes,
    i18n,
  });
  const formulaSections = useFormulaSections({excludedMetricTypes, userId, projectId, workspaceId, searchVerified});
  const savedMetricsSections = useMetricsSections({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    searchVerified,
  });
  const funnelSections = useMetricAndBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Funnel,
  });
  const retentionSections = useMetricAndBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Retention,
  });
  const simpleBehaviorSections = useMetricAndBehaviorSection({
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
    metricType: MetricType.Simple,
  });
  const {isLoading: isCohortsLoading, sections: cohortsSections} = useCohortsSections({
    dataGroupId,
    metricLevelDataGroups,
    dataGroupIdToNameMap,
    excludedMetricTypes,
    userId,
    projectId,
    workspaceId,
  });

  const isLoadingBehaviors = behaviors.isLoadingAll();
  const isLoadingMetrics = metrics.isLoadingAll();
  const isLoadingEvents = eventsController.loading;

  const isLoading = useMemo(() => {
    switch (category) {
      case SidebarCategory.Event:
      case SidebarCategory.CustomEvent:
        return isLoadingEvents;
      case SidebarCategory.Profile:
        return false;
      case SidebarCategory.Formula:
        return isLoadingMetrics;
      case SidebarCategory.Funnel:
        return isLoadingBehaviors;
      case SidebarCategory.Retention:
        return isLoadingBehaviors;
      case SidebarCategory.Simple:
        return isLoadingBehaviors;
      case SidebarCategory.Cohort:
        return isCohortsLoading;
      case SidebarCategory.Verified:
        return isLoadingEvents || isLoadingMetrics;
      default:
        return isCohortsLoading || isLoadingProperties || isLoadingBehaviors || isLoadingMetrics || isLoadingEvents;
    }
  }, [category, isCohortsLoading, isLoadingProperties, isLoadingBehaviors, isLoadingMetrics, isLoadingEvents]);
  let sections = useMemo(() => {
    if (isLoading) return [];
    if (selectedTagSet.size > 0) {
      if ([SidebarCategory.All, SidebarCategory.Event, SidebarCategory.CustomEvent].includes(category)) {
        return eventsSections;
      }
      return [];
    }

    switch (category) {
      case SidebarCategory.Event:
      case SidebarCategory.CustomEvent:
        return eventsSections;
      case SidebarCategory.Profile:
        return profileSections;
      case SidebarCategory.Formula:
        return formulaSections;
      case SidebarCategory.Metric:
        return savedMetricsSections;
      case SidebarCategory.Funnel:
        return funnelSections;
      case SidebarCategory.Retention:
        return retentionSections;
      case SidebarCategory.Simple:
        return simpleBehaviorSections;
      case SidebarCategory.Cohort:
        return cohortsSections;
      case SidebarCategory.Verified: {
        let recentSection = null;
        const allRecentItems = [
          MenuType.Event,
          MenuType.Formula,
          MenuType.Funnel,
          MenuType.Retention,
          MenuType.Simple,
          MenuType.SavedMetric,
        ]
          .filter((menuType) => !excludedMetricTypes.has(MenuTypeToMetricType[menuType]))
          .flatMap((menuType) => getRecentItems(cache, menuType))
          .filter(Boolean);

        if (allRecentItems.length > 0) {
          recentSection = {
            isRecentSection: true,
            label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
            items: allRecentItems.filter((item: SectionItem) => !!item.value.verified).slice(0, NUM_RECENT_ITEMS),
          };
        }

        return [
          ...(recentSection ? [recentSection] : []),
          ...eventsSections.filter((section) => !section.isRecentSection),
          {
            label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-formulas`),
            items: formulaSections.filter((section) => !section.isRecentSection).flatMap((section) => section.items),
          },
          {
            label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-simple-metrics-and-behaviors`),
            items: [
              ...simpleBehaviorSections
                .filter((section) => !section.isRecentSection)
                .flatMap((section) => section.items),
            ].filter((item) => {
              return !!item.value.verified;
            }),
          },
          {
            label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-funnel-metrics-and-behaviors`),
            items: [
              ...funnelSections.filter((section) => !section.isRecentSection).flatMap((section) => section.items),
            ].filter((item) => {
              return !!item.value.verified;
            }),
          },
          {
            label: ({t}) => t(`query-builder.multi-metrics-menu.verified.section-retention-metrics-and-behaviors`),
            items: [
              ...retentionSections.filter((section) => !section.isRecentSection).flatMap((section) => section.items),
            ].filter((item) => {
              return !!item.value.verified;
            }),
          },
        ];
      }
      default: {
        const formulaSectionItems = formulaSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const savedMetricSectionItems = savedMetricsSections.reduce(
          (sectionItems: MultiMetricsSectionItem[], section) => {
            if (section.items && !section.isRecentSection) {
              return [...sectionItems, ...section.items];
            }
            return sectionItems;
          },
          [],
        );
        const funnelSectionItems = funnelSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const retentionSectionItems = retentionSections.reduce((sectionItems: MultiMetricsSectionItem[], section) => {
          if (section.items && !section.isRecentSection) {
            return [...sectionItems, ...section.items];
          }
          return sectionItems;
        }, []);
        const simpleBehaviorSectionItems = simpleBehaviorSections.reduce(
          (sectionItems: MultiMetricsSectionItem[], section) => {
            if (section.items && !section.isRecentSection) {
              return [...sectionItems, ...section.items];
            }
            return sectionItems;
          },
          [],
        );
        const metricsSection: MultiMetricsSection = {
          label: ({t}) => t(`query-builder.multi-metrics-menu.section-saved-metrics`),
          items: orderBy(
            [
              ...savedMetricSectionItems,
              ...funnelSectionItems.filter((item) => item.menuType !== MenuType.SavedMetric),
              ...retentionSectionItems.filter((item) => item.menuType !== MenuType.SavedMetric),
              ...simpleBehaviorSectionItems.filter((item) => item.menuType !== MenuType.SavedMetric),
            ] as Array<
              SectionItem<SavedMetricMenuItemValue | BehaviorEntity, {menuType: MenuType; profileDataGroupId?: string}>
            >,
            (item) => item.value.created,
            `desc`,
          ),
          showLimit: 5,
        };

        const formulasSection: MultiMetricsSection = {
          label: ({t}) => t(`query-builder.multi-metrics-menu.section-saved-formulas`),
          items: [...formulaSectionItems],
        };

        let recentSection = null;
        let allRecentItems = [
          MenuType.Event,
          MenuType.Profile,
          MenuType.Formula,
          MenuType.Funnel,
          MenuType.Retention,
          MenuType.Simple,
          MenuType.Cohort,
          MenuType.SavedMetric,
        ]
          .filter((menuType) => !excludedMetricTypes.has(MenuTypeToMetricType[menuType]))
          .flatMap((menuType) => getRecentItems(cache, menuType))
          .filter(Boolean);

        // makes sure we omit saved metrics of excluded types
        if (excludedMetricTypes?.size) {
          allRecentItems = allRecentItems.filter((item) => {
            return !(
              item.menuType === MenuType.SavedMetric && excludedMetricTypes.has(item.value.definition.behavior?.type)
            );
          });
        }

        // Respect showAllEvents being false even in Recents
        if (!showAllEvents) {
          allRecentItems = allRecentItems.filter(
            (item: SectionItem) => item.value?.value !== SPECIAL_EVENTS.ALL_EVENTS,
          );
        }

        if (allRecentItems.length > 0) {
          recentSection = {
            isRecentSection: true,
            label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
            items: allRecentItems.slice(0, NUM_RECENT_ITEMS),
          };
        }

        return [
          ...(recentSection ? [recentSection] : []),
          metricsSection,
          formulasSection,
          ...eventsSections.filter((section) => !section.isRecentSection),
          ...profileSections,
          ...cohortsSections
            .filter((section) => !section.isRecentSection)
            .map((section) => ({...section, label: ({t}) => t(`query-builder.multi-metrics-menu.section-cohorts`)})),
        ].filter(Boolean);
      }
    }
  }, [
    dataGroupId,
    category,
    selected,
    isLoading,
    eventsSections,
    profileSections,
    formulaSections,
    funnelSections,
    retentionSections,
    cohortsSections,
  ]);

  sections = sections
    // filter out empty sections (e.g. empty recents section)
    .filter((section) => section.items.length > 0)
    .map((section) => ({
      ...section,
      hideTopDivider: false,
    }));

  return {
    isLoading,
    sections,
  };
}
